import { MessageService } from "@common/_services";
import { Button } from "@common/components/Atoms/Button";
import Error from "@common/components/Atoms/Error";
import {
    FormHandler,
    Rules,
    Select,
    TextArea,
} from "@common/components/Atoms/FormInputs";
import Modal from "@common/components/Molecules/Modal";
import { FormField } from "@common/models";
import React from "react";

interface SendMessageProps {
    isOpen: boolean;
    closeModal: () => void;
    onCancel: () => void;
    contextJobId?: number;
    contextJobName?: string;
    contextUserId?: number;
    contextUserFullName?: string;
    toUserIds: number[];
    messageId?: number;
    forceSelectedIds?: boolean;
    initialMessageBody?: string;
}

interface SendMessageState {
    error: boolean;
    errorMessages: string;
    formIsValid: boolean;
    processing: boolean;
    recipientOptions: string[];
    formFields: {
        recipients: FormField;
        message: FormField;
    };
}

export class SendMessage extends React.Component<SendMessageProps, SendMessageState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);

        this.emptyState = this.emptyState.bind(this);
        this.onFormFieldChange = this.onFormFieldChange.bind(this);
        this.sendClick = this.sendClick.bind(this);
        this.setRecipients = this.setRecipients.bind(this);
        this.handleError = this.handleError.bind(this);

        this.state = this.emptyState();
    }

    componentWillReceiveProps(nextProps: SendMessageProps) {
        // do we need to update?
        if (
            nextProps.isOpen &&
            (nextProps.isOpen !== this.props.isOpen ||
                nextProps.contextJobId !== this.props.contextJobId ||
                nextProps.contextUserId !== this.props.contextUserId)
        ) {
            this.setState(this.emptyState(), () =>
                this.setRecipients(
                    !!nextProps.contextJobId ? nextProps.contextJobId : 0,
                    !!nextProps.contextUserId ? nextProps.contextUserId : 0,
                    nextProps.toUserIds,
                    nextProps.messageId
                )
            );
        }
    }

    componentDidMount() {
        // was a job or user passed in on init
        if (this.props.isOpen) {
            this.setRecipients(
                !!this.props.contextJobId ? this.props.contextJobId : 0,
                !!this.props.contextUserId ? this.props.contextUserId : 0,
                this.props.toUserIds || [],
                this.props.messageId
            );
        }
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key => abortControllers[key].abort());
        }
    }

    emptyState(): SendMessageState {
        return {
            error: false,
            errorMessages: "",
            recipientOptions: [],
            processing: false,
            formIsValid: true,
            formFields: {
                recipients: {
                    name: "recipients",
                    label: "",
                    value: [] as string[],
                    isValid: true,
                    validationMessages: ["Please select recipient(s) for your message."],
                    rules: [Rules.required],
                },
                message: {
                    name: "message",
                    value: this.props.initialMessageBody,
                    label: "",
                    isValid: true,
                    validationMessages: ["Please enter your message/feedback."],
                    rules: [Rules.required],
                },
            },
        };
    }

    onFormFieldChange(name: string, value: string) {
        const formFields = this.state.formFields;
        FormHandler.handleInputChange(name, value, formFields);
        this.setState({ formFields, error: false });
    }

    sendClick() {
        const formFields = this.state.formFields;
        const isValid = FormHandler.validate(formFields);

        if (!isValid) {
            this.setState({ formFields, formIsValid: false });
        } else {
            this.setState({ formFields, formIsValid: true, processing: true }, () => {
                MessageService.send({
                    context_job_id: this.props.contextJobId,
                    context_user_id: this.props.contextUserId,
                    to_user_ids: this.state.formFields.recipients.value.toString().split(","),
                    body: this.state.formFields.message.value,
                    attachment_file_keys: [],
                })
                    .then(() => {
                        this.setState({ processing: false }, () => this.props.closeModal());
                    })
                    .catch(err => {
                        this.handleError(err);
                    });
            });
        }
    }

    // get a list of recipients based on the message context, pre-select the tousers passed in they are in the allowed recipient list
    setRecipients(
        contextJobId: number,
        contextUserId: number,
        selectedUserIds: number[],
        messageId?: number
    ) {
        const recipientToOptions = rcp => {
            return {
                label: rcp.name,
                value: rcp.user_id,
                clearableValue: !rcp.is_required,
            };
        };

        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.getRecipients) {
            this._abortControllers.getRecipients.abort();
        }
        this._abortControllers.getRecipients = new AbortController();
        const signal = this._abortControllers.getRecipients.signal;

        MessageService.getRecipients(contextJobId, contextUserId, messageId, signal).then(opts => {
            const reqs = opts.filter(x => x.is_required);
            const nonReqs = opts.filter(x => !x.is_required);

            // This ensures that the selectedUserIds appear as already selected
            const reducer = (acc, u) => {
                if (selectedUserIds && selectedUserIds.indexOf(u.user_id) >= 0) {
                    u.is_required = u.is_required || this.props.forceSelectedIds;
                    acc.push(u);
                }

                return acc;
            };

            const availableRecipients = opts.map(recipientToOptions);
            let selectedRecipients = nonReqs
                .reduce(reducer, reqs)
                .map(v => v.user_id)
                .join(",");
            if (selectedRecipients.length <= 0 && availableRecipients.length === 1) {
                selectedRecipients = availableRecipients[0].value;
            }

            // reset validation and state for a new set of props
            // TODO: set loading for modal?
            this.setState(prevState => ({
                recipientOptions: availableRecipients,
                formFields: {
                    ...prevState.formFields,
                    recipients: {
                        ...prevState.formFields.recipients,
                        value: selectedRecipients,
                    },
                },
            }));
        });
    }

    handleError(err) {
        let messages = err.responseData ? err.responseData.error_messages : [err.message];

        if (messages.length === 0) {
            messages = "An Error Occurred";
        }

        this.setState({
            error: true,
            processing: false,
            errorMessages: messages,
        });
    }

    render() {
        const multipleRecipients = this.state.recipientOptions.length > 1;
        const toLabel = multipleRecipients ? "to (multiple allowed)" : "to";

        return (
            <Modal
                isOpen={this.props.isOpen}
                closeModal={this.props.closeModal}
                loading={this.state.processing}>
                <div className="c-block">
                    <h2>
                        {this.props.contextJobName && this.props.contextJobName + " - "} send
                        message/feedback
                    </h2>
                    {this.props.contextUserFullName && <h3>{this.props.contextUserFullName}</h3>}

                    <Select
                        clearable={false}
                        name="recipients"
                        label={toLabel}
                        value={this.state.formFields.recipients.value}
                        isValid={this.state.formFields.recipients.isValid}
                        validationMessages={this.state.formFields.recipients.validationMessages}
                        onChange={this.onFormFieldChange}
                        options={this.state.recipientOptions}
                        multi={true}
                    />

                    <TextArea
                        autoComplete="Off"
                        readOnly={false}
                        name="message"
                        required={true}
                        label="Message/Feedback"
                        value={this.state.formFields.message.value}
                        maxLength={20480}
                        isValid={this.state.formFields.message.isValid}
                        validationMessages={this.state.formFields.message.validationMessages}
                        onChange={this.onFormFieldChange}
                    />
                </div>
                <div className="c-block">
                    <Error show={this.state.error} description={this.state.errorMessages} />
                </div>
                <div className="c-modal__footer">
                    <div className="c-modal__footer-left">
                        <Button
                            id="send-message-modal-send-button"
                            green={true}
                            onClick={this.sendClick}>
                            Send
                        </Button>
                    </div>
                    <div className="c-modal__footer-right">
                        <Button id="send-message-modal-cancel-button" onClick={this.props.onCancel}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }
}
